import { createSelector } from 'reselect';
import {
  PoiId,
  PocType,
  POC,
  LeadingAgentFeature,
  IOfficeAgentDisplayLevel,
  PromotionValues,
  MarketplaceType,
} from 'utils/entities';
import { State } from 'store/state';
import { getPOCName, contactFormInitialValuesSelector, checkEnabledLeadingAgentFeature, getPOCAvatar, getPOCLogo, getPOCCompanyName, getPOCExclusive, getPOCPhoneNumber, getPOCValidAgentPhoneWithPrefix, addDashesToPhoneNumber } from 'helpers/contact';
import { isBulletinPoiType, isProjectPoiType, isBulletin, Poi } from 'utils/marketplaceRoutes';
import { ContactUnionProps, ContactOwnProps, ContactType } from './types';
import { routeSelector, getRouteParams, marketplaceSelector } from 'store/state/selectors/router';
import { unitPageBulletinSelector, unitPageCommercialBulletinSelector, projectPageProjectSelector, projectPageCommercialProjectSelector, userProfileSelector } from 'store/state/domainData/selectors';
import { Route } from 'config/routes';
import { contactPageResponseSelector } from 'store/state/mutationsResponse/selectors';
import { unitPageLeadingAgentSelector } from 'store/state/domainData/selectors/unit-page-leading-data';
import { displayLevelAndPricingTierSelector } from 'store/state/domainData/selectors/agentWithBulletinsByIds';
import { PricingTierApi3, PromotionPlanType, LeadTypeApi3 } from 'store/sagas/apiService/types';
import { flow } from 'lodash';

export type ContactPoiGetter = (state: State, id: PoiId) => Poi;

export const poiSelector = createSelector([
  routeSelector,
  unitPageBulletinSelector,
  unitPageCommercialBulletinSelector,
  projectPageProjectSelector,
  projectPageCommercialProjectSelector,
], (route, bulletin, commercialBulletin, project, commercialProject) => {
  let poi = null;

  switch (route.name) {
    case Route.UnitPage:
      poi = bulletin;
      break;
    case Route.UnitPageCommercial:
      poi = commercialBulletin;
      break;
    case Route.ProjectPage:
      poi = project;
      break;
    case Route.ProjectPageCommercial:
      poi = commercialProject;
  }
  return poi;
});

export const leadApi3ContactType = createSelector([
  marketplaceSelector,
  (_: State, poi: Poi) => poi,
], (marketplace, poi): LeadTypeApi3 => {
  if (isBulletinPoiType(poi)) {
    const poc: POC = poi.poc;
    switch (poc.type) {
      case PocType.Private: {
        return marketplace === MarketplaceType.Commercial ? LeadTypeApi3.PRIVATE_COMMERCIAL_BULLETIN : LeadTypeApi3.PRIVATE_RESIDENTIAL_BULLETIN;
      }
      case PocType.Agent: {
        return marketplace === MarketplaceType.Commercial ? LeadTypeApi3.AGENT_COMMERCIAL_BULLETIN : LeadTypeApi3.AGENT_RESIDENTIAL_BULLETIN;
      }
    }
  }

  if (isProjectPoiType(poi)) return LeadTypeApi3.PROJECT;
  return null;
});

const isGalleryOpenSelector = flow(getRouteParams, (p) => p.activeListingImage !== undefined);
const POI_PREMIUM_OR_ADVANCED = new Set([ PromotionPlanType.PREMIUM, PromotionPlanType.ADVANCE ]);

export function makeContactMapStateToPropsFactory(poiByIdSelector: ContactPoiGetter) {
  return () => (state: State, props: ContactOwnProps): ContactUnionProps => {
    const poi = poiByIdSelector(state, props.id);

    const isGalleryOpen = isGalleryOpenSelector(state);
    const contactValues = contactFormInitialValuesSelector(state);
    const leadApi3Type = leadApi3ContactType(state, poi);
    const contactResponse = contactPageResponseSelector(state, props.id);
    const isContacted = Boolean(contactResponse && contactResponse.data && contactResponse.data.contactV4);

    if (isBulletinPoiType(poi)) {
      const poc: POC = poi.poc;
      switch (poc.type) {
        case PocType.Private: {
          const user = userProfileSelector(state);
          const name = getPOCName(poc);
          const phone = flow(getPOCPhoneNumber, addDashesToPhoneNumber)(poc);
          const whatsappNumber = getPOCValidAgentPhoneWithPrefix(poc);
          // be sure to use variable below for commercial
          const isPremiumOrAdvanced = isBulletin(poi) ? POI_PREMIUM_OR_ADVANCED.has((poi).promotionPlanType) : true;
          return {
            id: poi.id,
            poiType: poi.type,
            cardType: ContactType.Private,
            phone,
            poi,
            name,
            contactValues,
            isContacted,
            isGalleryOpen,
            leadApi3Type,
            whatsappNumber: isPremiumOrAdvanced ? whatsappNumber : undefined,
            user,
          };
        }
        case PocType.Agent: {
          const user = userProfileSelector(state);
          const leadingData = unitPageLeadingAgentSelector(state);
          const agentData = displayLevelAndPricingTierSelector(state);
          const isLeadingAgent = checkEnabledLeadingAgentFeature(leadingData, LeadingAgentFeature.ContactForm);
          const hideAgentInfo = agentData && agentData.displayLevel === IOfficeAgentDisplayLevel.Minimal && agentData.pricingTier !== PricingTierApi3.Discounted;
          const isDiscounted = agentData && agentData.pricingTier === PricingTierApi3.Discounted;
          const showOnlyOffice = poc.showOnlyOffice || hideAgentInfo;
          const whatsappNumber = getPOCValidAgentPhoneWithPrefix(poi.poc);
          const licenseId = agentData && agentData.licenseId || null;

          return {
            id: poi.id,
            poiType: poi.type,
            cardType: ContactType.Agent,
            contactValues,
            officeId: poc.officeId,
            agentId: poc.agentId,
            isLeadingAgent,
            showOnlyOffice,
            hideAgentInfo,
            leadApi3Type,
            isExclusive: getPOCExclusive(poc),
            agentName: getPOCName(poc),
            officeName: getPOCCompanyName(poc),
            agentLogo: getPOCAvatar(poc),
            officeLogo: getPOCLogo(poc),
            phone: flow(getPOCPhoneNumber, addDashesToPhoneNumber)(poc),
            isDiscounted,
            isContacted,
            whatsappNumber,
            poi,
            user,
            isGalleryOpen,
            licenseId,
          };
        }
      }
    }

    if (isProjectPoiType(poi)) {
      return {
        id: poi.id,
        poiType: poi.type,
        developers: poi.developers,
        projectName: poi.projectName,
        projectLogo: poi.projectLogo,
        cardType: ContactType.Developer,
        isPromoted: poi.promotionStatus && poi.promotionStatus.status === PromotionValues.Promoted,
        contactValues,
        leadApi3Type,
        poi,
        isGalleryOpen,
        isContacted,
        phone: poi.phoneNumber && poi.phoneNumber.length && poi.phoneNumber[0],
      };
    }

    return null;
  };
}
